import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Section from '../components/Section';
import Container from '../components/Container';
import Button from '../components/Button';
import ramadanImage from '../assets/ramadan.avif';
import foodImage from '../assets/food.avif';

// Enhanced styled components for a premium nonprofit appearance
const Hero = styled.div`
  background: linear-gradient(to right, rgba(0, 82, 156, 0.7), rgba(40, 126, 60, 0.7)), 
              url('https://images.unsplash.com/photo-1593113616828-6f22bca04804?ixlib=rb-1.2.1&auto=format&fit=crop&q=80');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: ${({ theme }) => theme.colors.light};
  height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 10vh;
    background: linear-gradient(to top, ${({ theme }) => theme.colors.light}, transparent);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 50vh;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  padding: 0 ${({ theme }) => theme.spacing.md};
  z-index: 2;
  color: #fff;
  
  h1 {
    font-size: ${({ theme }) => theme.fontSizes.huge};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    text-shadow: 0 2px 4px rgba(0,0,0,0.2);
    color: #fff;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes.xxxl};
    }
  }
  
  p {
    font-size: ${({ theme }) => theme.fontSizes.lg};
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
    text-shadow: 0 1px 2px rgba(0,0,0,0.1);
    color: #fff;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes.md};
    }
  }
`;

const ProjectSection = styled(Section) <{ alternate?: boolean }>`
  background-color: ${({ alternate, theme }) =>
    alternate ? theme.colors.background : theme.colors.light};
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary}, ${({ theme }) => theme.colors.accent});
    opacity: ${({ alternate }) => alternate ? '0.7' : '0.3'};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xxl} 0;
  }
`;

const OverviewSection = styled(Section)`
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
  text-align: center;
  
  h2 {
    position: relative;
    display: inline-block;
    margin-bottom: ${({ theme }) => theme.spacing.xl};
    
    &:after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 3px;
      background-color: ${({ theme }) => theme.colors.accent};
    }
  }
  
  p {
    max-width: 800px;
    margin: 0 auto;
    font-size: ${({ theme }) => theme.fontSizes.lg};
    line-height: 1.6;
  }
`;

const FlexRow = styled.div<{ reverse?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: ${({ reverse }) => reverse ? 'row-reverse' : 'row'};
    align-items: center;
  }
`;

const ProjectContent = styled.div`
  flex: 1;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex: 3;
    padding-right: ${({ theme }) => theme.spacing.xl};
    padding-left: ${({ theme }) => theme.spacing.xl};
  }
`;

const ProjectImage = styled.div`
  flex: 1;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  position: relative;
  
  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    box-shadow: ${({ theme }) => theme.shadows.md};
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    
    &:hover {
      transform: scale(1.02);
      box-shadow: ${({ theme }) => theme.shadows.lg};
    }
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 20px;
    left: 20px;
    right: -20px;
    bottom: -20px;
    border: 2px solid ${({ theme }) => theme.colors.accent};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    z-index: -1;
    opacity: 0.5;
    pointer-events: none;
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex: 2;
    margin-bottom: 0;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    img {
      height: 300px;
    }
    
    &:after {
      display: none;
    }
  }
`;

const ProjectTitle = styled.h2`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  position: relative;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ProjectSubtitle = styled.h3`
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSizes.xl};
`;

const BulletList = styled.ul`
  padding-left: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  li {
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    position: relative;
    padding-left: ${({ theme }) => theme.spacing.sm};
    line-height: 1.6;
    
    &:before {
      content: '•';
      color: ${({ theme }) => theme.colors.accent};
      font-weight: bold;
      display: inline-block;
      width: 1em;
      margin-left: -1em;
    }
  }
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  margin: ${({ theme }) => theme.spacing.xl} 0;
`;

const StatItem = styled.div`
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  text-align: center;
  flex: 1;
  min-width: 180px;
  border-bottom: 3px solid ${({ theme }) => theme.colors.accent};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  h3 {
    font-size: 3rem;
    color: ${({ theme }) => theme.colors.accent};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
  }
  
  p {
    color: ${({ theme }) => theme.colors.dark};
    font-weight: ${({ theme }) => theme.fontWeights.semiBold};
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const CtaSection = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.xxl} 0 ${({ theme }) => theme.spacing.xl};
  position: relative;
  
  h2 {
    font-size: ${({ theme }) => theme.fontSizes.xxl};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
  
  p {
    max-width: 600px;
    margin: 0 auto ${({ theme }) => theme.spacing.lg};
    font-size: ${({ theme }) => theme.fontSizes.lg};
    line-height: 1.6;
  }
`;

const OurProjects: React.FC = () => {
  return (
    <>
      <Hero>
        <HeroContent>
          <h1>Our Projects</h1>
          <p>Discover the transformative initiatives changing lives across the Bay Area through sustainable empowerment and community support.</p>
        </HeroContent>
      </Hero>

      <OverviewSection>
        <Container>
          <h2>Projects Overview</h2>
          <p>
            At Project Assist, we focus on creating sustainable solutions that address immediate needs while building pathways to long-term self-sufficiency. Our initiatives span from emergency response during COVID-19 to ongoing refugee resettlement programs, all with the goal of empowering individuals and families to thrive.
          </p>
        </Container>
      </OverviewSection>

      <ProjectSection alternate>
        <Container>
          <FlexRow>
            <ProjectContent>
              <ProjectTitle>COMMUNITY-LED RESETTLEMENT INITIATIVE</ProjectTitle>
              <p>
                Project Assist, in partnership with local organizations are helping families who had to leave their countries to resettle in Northern California. Often families that were forced to move, leave behind family members, jobs, businesses and all that they hold dear. Almost half of the refugees who fled are children. Your support will not only provide them the essentials they need to survive initially (e.g. food, clothing, housing etc) but will also help longer-term to sustain a better life through access to opportunities (e.g. education/retraining, job seekers support, public services etc).
              </p>
              <ProjectSubtitle>How your donation will help:</ProjectSubtitle>
              <p>
                Your donation will support their initial 6 months to settle a family includes but not limited to the following:
              </p>
              <BulletList>
                <li>Secure housing</li>
                <li>Provide basic necessities (furniture, clothing, food, etc.)</li>
                <li>Legal assistance in completion of relevant immigration documentation and to access federal/state allowances (where eligible)</li>
                <li>Connect to relevant services, including health care, education, and legal assistance</li>
                <li>English language skills</li>
                <li>Help to secure employment; and</li>
                <li>Provide community orientation, friendship, and moral support</li>
                <li>Computers, transportation and other support needed for children and family members for school and work.</li>
              </BulletList>
              <Button variant="accent" size="large" as={Link} to="/donate">Donate Now</Button>
            </ProjectContent>
            <ProjectImage>
              <img
                src="https://images.unsplash.com/photo-1593113630400-ea4288922497?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                alt="Food bank volunteers sorting donations for distribution"
              />
            </ProjectImage>
          </FlexRow>
        </Container>
      </ProjectSection>

      <ProjectSection>
        <Container>
          <FlexRow reverse>
            <ProjectContent>
              <ProjectTitle>RAMADAN AND BEYOND 2020 - 2021 - COVID-19 SUPPORT</ProjectTitle>
              <p>
                Helped the broader community members who were facing significant financial loss during COVID. During the second year of the pandemic and the community trying to celebrate a socially distant Ramadan and Eid-ul-Fitr, a lot of people were experiencing difficulties due to job loss and lack of family support. The goal of our Ramadan and Beyond 2021 initiative was to provide food and basic necessities to families who were struggling with daily living expenses. Through donations from family and friends in the community, we were able to help us share the blessings of Ramadan and joy of Eid even in the times of the pandemic.
              </p>
              <p>
                Approximately $30,467 were raised in donations in addition to help with Eid gifts, clothing, books and other household items within just two months. Thank you to all who supported our efforts to help the community in need.
              </p>

              <Stats>
                <StatItem>
                  <h3>63</h3>
                  <p>Families Helped</p>
                </StatItem>
                <StatItem>
                  <h3>53</h3>
                  <p>Zakat Eligible</p>
                </StatItem>
                <StatItem>
                  <h3>$30K+</h3>
                  <p>Raised</p>
                </StatItem>
              </Stats>

              <ProjectSubtitle>How We Helped:</ProjectSubtitle>
              <BulletList>
                <li>10 individuals</li>
                <li>15 couples</li>
                <li>8 families of 3</li>
                <li>15 families of 4</li>
                <li>11 families of 5</li>
                <li>2 families with 6 and 2 with 8 family members including children</li>
                <li>Cheques to address immediate financial need sent to 59 families / individuals</li>
                <li>4 visa gift cards</li>
                <li>Additional assistance providing a laptop, clothes and diapers for children and food parcels to some of the families based on specific needs</li>
              </BulletList>
            </ProjectContent>
            <ProjectImage>
              <img
                src={ramadanImage}
                alt="Ramadan and COVID-19 relief efforts"
              />
            </ProjectImage>
          </FlexRow>
        </Container>
      </ProjectSection>

      <ProjectSection alternate>
        <Container>
          <FlexRow>
            <ProjectContent>
              <ProjectTitle>COMMUNITY-LED RESETTLEMENT INITIATIVE 2023-2024</ProjectTitle>
              <p>
                Project Assist, in partnership with local organizations helped families who had to leave their countries to resettle in Northern California. Often families that were forced to move, leave behind family members, jobs, businesses and all that they hold dear. Almost half of the refugees who fled are children. The support provided them the essentials to fulfill immediate needs (e.g. food, clothing, housing etc) and also helped longer-term to sustain a better life through access to opportunities (e.g. education/retraining, job seekers support, public services etc).
              </p>

              <ProjectSubtitle>Project Goal:</ProjectSubtitle>
              <p>
                Donations and volunteer time for at least first 6 months to settle a family including but not limited to the following:
              </p>
              <BulletList>
                <li>Secure temporary housing</li>
                <li>Provide basic necessities (furniture, clothing, food, etc.)</li>
                <li>Help with completion of relevant immigration documentation and to access information regarding federal/state allowances (where eligible)</li>
                <li>Connect to relevant services, including health care, education, and legal assistance</li>
                <li>English language skills resources as needed</li>
                <li>Help to secure employment by making connection</li>
                <li>Provide community orientation, friendship, and moral support</li>
                <li>Computers, transportation (car donations) and other support needed for children and family members for school and work.</li>
              </BulletList>

              <p><strong>RAISED up to $50,000</strong></p>

              <ProjectSubtitle>Success Stories:</ProjectSubtitle>
              <BulletList>
                <li>Helped resettle a family of 8 living out of a trailer to move with their extended family to Africa. In addition to arranging funds for the family to move back, we were able to help the family with eid gifts for the kids and cash assistance to support through this transition.</li>
                <li>We successfully raised funds to help settle an Afghan Refugee family with housing, rent support, transportation, living essentials and continuing education. The family included a young couple with a baby, the husband's sister and elderly mother. They are now well settled and sustaining themselves without need for support or further financial assistance. This family has become a contributing part of the community now helping others in need from their own experiences during resettlement.</li>
                <li>A family of 4 who recently arrived from Gaza had not received enough funds for housing and living expenses. We were primarily focused on setting them up for the first 6 months through housing assistance, medical care, employment, transportation and educational support. The goal is to help them become self-sufficient so they can sustain themselves as they settle here.</li>
              </BulletList>

              <Button variant="accent" size="large" as={Link} to="/donate">Support Our Resettlement Initiative</Button>
            </ProjectContent>
            <ProjectImage>
              <img
                src={foodImage}
                alt="Food distribution for resettled families"
              />
            </ProjectImage>
          </FlexRow>
        </Container>
      </ProjectSection>

      <Section>
        <Container>
          <CtaSection>
            <h2>Join Us in Making a Difference</h2>
            <p>Your support can help families rebuild their lives and create pathways to self-sufficiency.</p>
            <Button variant="accent" size="large" as={Link} to="/donate">Donate Now</Button>
          </CtaSection>
        </Container>
      </Section>
    </>
  );
};

export default OurProjects; 